import Vue from 'vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import utils from '@/mixins/utils.js'
import store from '@/store/store'
import { vue } from '@/main.js'

/**
 * Retorna os cadastros de uma equipe
 *
 * @param {int} cod_ibge - IBGE do município
 * @param {int} nu_ine - INE da Equipe
 *
 * @returns {Array}
 */
export async function getCadastrosPorEquipe(cod_ibge, nu_ine) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/acoes-interprofissionais/${cod_ibge}/equipe/${nu_ine}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna os cadastros por microárea
 *
 * @param {*} cod_ibge
 * @param {*} nu_ine
 * @param {*} micro_area
 *
 * @returns {Array}
 */
export async function getCadastrosPorMicroArea(cod_ibge, nu_ine, micro_area) {
  try {
    const { data } = await Vue.prototype.$api.get(
      `/app/acoes-interprofissionais/${cod_ibge}/equipe/${nu_ine}/microarea/${micro_area}`
    )
    return data
  } catch (err) {
    console.log(err)
  }
}

/**
 * Retorna o nome da cor de acordo com o valor
 * @param {Number} valor - Valor numérico
 * @returns {String}
 */
export function getCorTextoResultado(valor) {
  if (valor >= 60) {
    return 'green-color'
  } else {
    return 'red-color'
  }
}

/**
 * Retorna os pares mês/ano considerados no filtro de período.
 *
 * Inclui o período selecionado e os 3 meses anteriores ao primeiro mês:
 * - Mês: janeiro/2026 → out/2025, nov/2025, dez/2025 e jan/2026
 * - Quadrimestre: 2º (mai–ago) → fev, mar, abr + mai, jun, jul, ago
 *
 * @param {Object|null} mes Mês/período selecionado.
 * @returns {Array<{nu_mes: Number, nu_ano: Number}>}
 */
export function obterPeriodosFiltro(mes) {
  if (!mes) {
    return []
  }

  const mesesSelecionados = (mes.value || []).map(Number)
  const anoSelecionado = Number(mes.nu_ano)

  if (!mesesSelecionados.length) {
    return []
  }

  const periodos = mesesSelecionados.map((nu_mes) => ({
    nu_mes,
    nu_ano: anoSelecionado,
  }))

  const primeiroMes = Math.min(...mesesSelecionados)
  let mesAtual = primeiroMes
  let anoAtual = anoSelecionado

  for (let i = 0; i < 3; i++) {
    mesAtual -= 1
    if (mesAtual < 1) {
      mesAtual = 12
      anoAtual -= 1
    }
    periodos.unshift({ nu_mes: mesAtual, nu_ano: anoAtual })
  }

  return periodos
}

/**
 * Verifica se o mês/ano do registro está no período filtrado.
 *
 * @param {Array<{nu_mes: Number, nu_ano: Number}>} periodos
 * @param {Number|String} nu_mes
 * @param {Number|String} nu_ano
 * @returns {Boolean}
 */
export function periodoInclui(periodos, nu_mes, nu_ano) {
  return periodos.some(
    (periodo) =>
      periodo.nu_mes === Number(nu_mes) && periodo.nu_ano === Number(nu_ano)
  )
}

/**
 * Retorna o tipo de ação a contabilizar conforme os filtros da legenda.
 *
 * @param {Boolean} exibirEspecificas
 * @param {Boolean} exibirCompartilhadas
 * @returns {'especificas'|'compartilhadas'|'todas'}
 */
export function getTipoAcao(exibirEspecificas, exibirCompartilhadas) {
  if (exibirEspecificas) return 'especificas'
  if (exibirCompartilhadas) return 'compartilhadas'
  return 'todas'
}

/**
 * Retorna o nome da coluna de quantidade conforme o tipo de ação.
 *
 * @param {'especificas'|'compartilhadas'|'todas'} tipoAcao
 * @returns {String}
 */
export function getNomeColunaAcoes(tipoAcao) {
  if (tipoAcao === 'especificas') return 'Especificas'
  if (tipoAcao === 'compartilhadas') return 'Compartilhadas'
  return 'Ações'
}

/**
 * Retorna a quantidade de ações conforme o filtro.
 *
 * - especificas: conta registros com nm igual a 0.
 * - compartilhadas: conta registros com nm diferente de 0.
 * - todas: conta os dois tipos.
 * Se houver mês selecionado, conta apenas os registros do período
 * (incluindo os 3 meses anteriores ao primeiro mês).
 * Se houver equipe selecionada, conta apenas os registros dessa equipe.
 *
 * @param {Object} item Cadastro.
 * @param {Object|null} mes Mês/período selecionado.
 * @param {'especificas'|'compartilhadas'|'todas'} tipoAcao Tipo de ação a contar.
 * @param {Object|null} equipe Equipe selecionada.
 * @returns {Number} Quantidade de ações.
 */
export function getQuantidadeAcoes(
  item,
  mes = null,
  tipoAcao = 'todas',
  equipe = null
) {
  let dados = []

  try {
    dados =
      typeof item.dados === 'string' ? JSON.parse(item.dados) : item.dados || []
  } catch (e) {
    dados = []
  }

  const ineEquipe = equipe?.ine_emulti

  return dados.filter((proc) => {
    const isEspecifica = Number(proc.nm) === 0

    if (tipoAcao === 'especificas' && !isEspecifica) {
      return false
    }

    if (tipoAcao === 'compartilhadas' && isEspecifica) {
      return false
    }

    if (
      ineEquipe != null &&
      ineEquipe !== '' &&
      String(proc.nu_ine) != String(ineEquipe)
    ) {
      return false
    }

    if (!mes) {
      return true
    }

    return periodoInclui(obterPeriodosFiltro(mes), proc.nu_mes, proc.nu_ano)
  }).length
}

/**
 * Gera o relatório em PDF.
 *
 * @param {Array} cadastros
 * @param {Object} selectEquipe
 * @param {String} microArea
 * @param {Object|String|Number|null} mes Mês selecionado no filtro.
 * @param {'especificas'|'compartilhadas'|'todas'} tipoAcao Tipo de ação a contar.
 */
export function generatePdf(
  cadastros,
  selectEquipe,
  microArea,
  mes = null,
  tipoAcao = 'todas'
) {
  let title_pdf = 'Ações interprofissionais'
  let titlePdfSave = utils.methods.titlePdf('Ações interprofissionais')

  let description_pdf = 'Ações realizadas por profissionais da eMulti'
  const colunaQuantidade = getNomeColunaAcoes(tipoAcao).toUpperCase()
  let headers_pdf = ['NOME', 'NASCIMENTO', 'MÃE', colunaQuantidade]
  var data_pdf = []
  for (let i = 0; i < cadastros.length; i++) {
    var obj = {}
    let item = cadastros[i]

    obj.no_cidadao = item.no_cidadao ?? '-'
    obj.dt_nascimento =
      item.dt_nascimento != null
        ? utils.methods.formatDate(item.dt_nascimento)
        : '-'
    obj.no_mae = item.no_mae ?? '-'
    obj.acoes = getQuantidadeAcoes(item, mes, tipoAcao, selectEquipe)
    obj.num_acoes_inter_prof = item.num_acoes_inter_prof ?? 0

    data_pdf.push(obj)
  }

  let doc = new jsPDF({
    orientation: 'l',
    unit: 'pt',
    format: 'a4',
    ...utils.methods.encryptionPdf(),
  })
  // cria a tabela
  let table = utils.methods.createTablePdf(data_pdf)

  utils.methods
    .fileToBase64(store.state.infoMunicipio.brasao)
    .then((base64data) => {
      // Header
      let verticalPosition = utils.methods.headerPdf(
        doc,
        base64data,
        title_pdf,
        description_pdf,
        200,
        'l'
      )

      const nomeMes = utils.methods.getDescricaoMes(mes)

      verticalPosition = utils.methods.bodyPdf(
        doc,
        [
          {
            label: 'Período',
            value: nomeMes,
          },
          {
            label: 'Equipe',
            value: selectEquipe.no_equipe,
          },
          {
            label: 'CNES',
            value: selectEquipe.nu_cnes,
          },
          {
            label: 'INE',
            value: selectEquipe.nu_ine,
          },
          {
            label: 'Microárea',
            value: microArea,
          },
        ],
        verticalPosition,
        'l'
      )

      doc.autoTable({
        head: [headers_pdf],
        body: table,
        headStyles: {
          fillColor: '#ccc',
          fontSize: 6,
          textColor: '#000',
          halign: 'center',
          cellWidth: 'wrap',
        },
        bodyStyles: {
          halign: 'center',
          fontSize: 6,
        },
        theme: 'striped',
        startY: verticalPosition,
        showHead: 'firstPage',
        willDrawCell: function (data) {
          let num_acoes_inter_prof = data.row.raw[4]
          if (num_acoes_inter_prof == 0) {
            doc.setTextColor(255, 165, 0) // laranja
          } else {
            doc.setTextColor(2, 99, 57) // verde
          }
          // pinta a cor da descrição das colunas de preto
          if (data.row.section === 'head') {
            doc.setTextColor(0, 0, 0)
          }
        },
      })

      utils.methods.footerPdf(doc, doc.autoTable.previous.finalY, 250, 600, 'l')

      doc.save(titlePdfSave + '.pdf')

      vue.$toast.success('PDF GERADO COM SUCESSO!', 'Sucesso:', {
        position: 'topRight',
      })
    })
    .catch((error) => {
      console.error(error)
      vue.$toast.error('ERRO AO GERAR PDF!', 'Erro:', {
        position: 'topRight',
      })
    })
}

function getMesAnoAtual() {
  const hoje = new Date()
  const mesAtual = hoje.getMonth() + 1
  const anoAtual = String(hoje.getFullYear())

  return `${mesAtual}/${anoAtual}`
}

function montarResultado(dados, tipoResultado = 'quadrimestral') {
  if (tipoResultado === 'mensal') {
    return {
      numerador: dados.numerador,
      denominador: dados.denominador,
      resultado: dados.resultado,
      classificacao: dados.classificacao,
    }
  }

  return {
    numerador: dados.numerador_quad,
    denominador: dados.denominador_quad,
    resultado: dados.resultado_quad,
    classificacao: dados.classificacao_quad,
  }
}

export function percentualMunicipio(
  dadosDaTabelaResultado,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDoMunicipio = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 1 &&
      item.ibge === store.state.cod_ibge &&
      item.indicador == 45 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDoMunicipio) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDoMunicipio.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualEquipe(
  dadosDaTabelaResultado,
  nuIne,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaEquipe = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 2 &&
      item.ine === nuIne &&
      item.indicador == 45 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaEquipe) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaEquipe.dados)

  return montarResultado(dados, tipoResultado)
}

export function percentualMicroArea(
  dadosDaTabelaResultado,
  nu_ine,
  micro_area,
  tipoResultado = 'quadrimestral'
) {
  const mesAnoAtual = getMesAnoAtual()

  const dadosDaMicroArea = dadosDaTabelaResultado.find(
    (item) =>
      item.nivel == 3 &&
      item.ine == nu_ine &&
      item.micro_area === micro_area &&
      item.indicador == 45 &&
      item.mes_ano === mesAnoAtual
  )

  if (!dadosDaMicroArea) {
    return {
      numerador: 0,
      denominador: 0,
      resultado: 0,
      classificacao: '',
    }
  }

  const dados = utils.methods.parseObjectJson(dadosDaMicroArea.dados)

  return montarResultado(dados, tipoResultado)
}
